"use client";

import { useStytch, useStytchSession, useStytchUser } from "@stytch/nextjs";
import { useRouter } from "next/navigation";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import type { User } from "stytch";
import { SWRConfig, mutate } from "swr";
// @ts-ignore
import useLocalStorageState from "use-local-storage-state";
import { SHORT_SESSION_DURATION_MINUTES, SHORT_SESSION_ORG_IDS } from "../env";
import ErrorPage from "../errors/error-page";
import { useProfile } from "../hooks/use-profile";
import { isJWTExpired } from "../hooks/use-stytch-jwt";
import type { Profile, ProfileOrg } from "../models";
import { useLayoutPreferences } from "./preferences-provider";
import { identifyUser } from "./posthog/posthog-client-provider";

export const UserContext = createContext<UserContextProps>({
  isReady: false,
  isStytchInitialized: false,
  isProfileLoading: false,
  buildRequestHeaders: async () => ({}),
  logout: async () => {
    // pass
  },
  setPauseFetchProfile: () => {
    throw new Error("setPauseFetchProfile must be used within a UserProvider");
  },
  isVercelManagedOrg: false,
});

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export interface UserContextProps {
  isReady: boolean;
  isProfileLoading: boolean;
  isStytchInitialized: boolean;
  stytchUser?: User;
  isStytchUserInitialized?: boolean;
  profile?: Profile;
  currentOrg?: string;
  currentOrgFull?: ProfileOrg;
  isGroqUser?: boolean;
  changeOrg?: (orgId: string) => void;
  buildRequestHeaders: (options?: { noContentType: boolean }) => Promise<
    Record<string, string>
  >;
  logout: (options?: { redirect?: boolean }) => Promise<void>;
  setPauseFetchProfile: (pause: boolean) => void;
  isVercelManagedOrg: boolean;
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const stytch = useStytch();
  const { isInitialized } = useStytchSession();
  const { user, isInitialized: isStytchUserInitialized } = useStytchUser();

  const { getLayoutPreference, setLayoutPreference, clearLayoutPreferences } =
    useLayoutPreferences();

  let sessionJwt: string | undefined;
  if (typeof window !== "undefined" && isInitialized && user) {
    sessionJwt = stytch.session.getTokens()?.session_jwt;
  }

  // during auth/signup, we pause the profile fetch until the profile is created.
  const [pauseFetchProfileSince, setPauseFetchProfileSince] =
    useLocalStorageState("pause-fetch-profile-since-ts", {
      defaultValue: 0,
    });

  // if for some reason the local storage ts is not cleared, this will clear it after 5 seconds
  const pausedFetchProfile = Date.now() - pauseFetchProfileSince < 5_000;
  const setPauseFetchProfile = (pause: boolean) => {
    if (pause) {
      setPauseFetchProfileSince(Date.now());
    } else {
      setPauseFetchProfileSince(0);
    }
  };

  const {
    data,
    error,
    isLoading: isProfileLoading,
  } = useProfile(pausedFetchProfile ? undefined : sessionJwt);
  const { profile, errorCode, status } = data || {
    profile: null,
    unauthenticatedError: false,
  };
  const router = useRouter();
  const logout = async ({ redirect = true }: { redirect?: boolean } = {}) => {
    clearLayoutPreferences("current-org");
    await stytch.session.revoke();
    if (redirect) {
      router.push("/");
    }
  };

  const defaultOrg = profile?.user.orgs.data.find(() => true)?.id || "";
  const [currentOrg, setCurrentOrg] = useState<string>(
    (getLayoutPreference("current-org") as string) || defaultOrg,
  );

  const currentOrgFull = profile?.user?.orgs?.data.find(
    (org) => org.id === currentOrg,
  );

  const isVercelManagedOrg = currentOrgFull?.vercel_managed === true;

  const changeOrg = (orgId: string) => {
    setCurrentOrg(orgId);
    setLayoutPreference("current-org", orgId);
    // Run the mutate after the current org is set
    setTimeout(() => {
      mutate(
        (_key) => true, // which cache keys are updated
        undefined, // update cache data to `undefined`
      );
    }, 0);
  };

  const buildRequestHeaders = async (opts?: { noContentType: boolean }) => {
    let sessionJwt = stytch.session.getTokens()?.session_jwt;

    if (!sessionJwt || isJWTExpired(sessionJwt)) {
      await stytch.session.authenticate();
      sessionJwt = stytch.session.getTokens()?.session_jwt;
    }

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const result: any = {
      authorization: `Bearer ${sessionJwt}`,
      "groq-organization": currentOrg || "",
    };

    if (!opts?.noContentType) {
      result["Content-Type"] = "application/json";
    }

    return result;
  };

  useEffect(() => {
    // If the JWT is expired, it will be auto refreshed by stytch, do nothing
    if (errorCode === "invalid_or_expired_jwt") {
      // otherwise, the jwt is busted, log the user out.
    } else if (status === 401) {
      logout();
    }
  }, [errorCode, status]);

  const isOrgValid = (): boolean => {
    if (!profile) {
      return false;
    }
    return !!profile.user.orgs.data.find((org) => org.id === currentOrg);
  };
  useEffect(() => {
    if (profile) {
      const defaultToFirstOrg = () => {
        if (profile.user.orgs.data[0]) {
          setCurrentOrg(profile.user.orgs.data[0].id);
          setLayoutPreference("current-org", profile.user.orgs.data[0].id);
        }
      };

      if (currentOrg) {
        // Check the current org is still valid
        const org = profile.user.orgs.data.find((org) => org.id === currentOrg);
        if (!org) {
          defaultToFirstOrg();
        }
      } else {
        defaultToFirstOrg();
      }
    }
  }, [profile]);

  useEffect(() => {
    if (!(profile && currentOrg)) {
      return;
    }
    const payload = {
      organization_id: currentOrg,
      user_id: profile?.user.id,
    };
    // @ts-ignore
    if (typeof window !== "undefined" && window.gtag) {
      // @ts-ignore
      window.gtag("set", payload);
    }

    identifyUser(profile);
  }, [currentOrg, profile]);

  const isGroqUser = user?.emails?.some((email) =>
    email.email.endsWith("@groq.com"),
  );

  const enforceSessionDuration = ({ currentOrg }: { currentOrg: string }) => {
    if (currentOrg && SHORT_SESSION_ORG_IDS.includes(currentOrg)) {
      if (stytch.session.getInfo().session?.expires_at) {
        const expiresAt = new Date(
          stytch.session.getInfo().session?.expires_at || "",
        );
        const now = new Date();
        const diff = expiresAt.getTime() - now.getTime();
        const diffMinutes = diff / (1000 * 60);
        if (diffMinutes > SHORT_SESSION_DURATION_MINUTES) {
          stytch.session.authenticate({
            session_duration_minutes: SHORT_SESSION_DURATION_MINUTES,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (isInitialized && profile) {
      enforceSessionDuration({ currentOrg });
    }
  }, [isInitialized, currentOrg, profile]);

  return (
    <SWRConfig value={{ revalidateOnFocus: false }}>
      <UserContext.Provider
        value={{
          isStytchInitialized: isInitialized,
          isReady:
            !!profile &&
            !!currentOrg &&
            isOrgValid() &&
            !!stytch.session.getTokens()?.session_jwt &&
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            !isJWTExpired(stytch.session.getTokens()?.session_jwt!),
          stytchUser: user as User,
          isStytchUserInitialized,
          profile: profile || undefined,
          isProfileLoading,
          isGroqUser,
          currentOrg,
          currentOrgFull,
          changeOrg,
          buildRequestHeaders,
          logout,
          setPauseFetchProfile,
          isVercelManagedOrg,
        }}
      >
        {error && <ErrorPage error={error} />}
        {!error && children}
      </UserContext.Provider>
    </SWRConfig>
  );
};
